import _ from 'underscore'
import Poll from './poll'
import SegmentingOptions from './segmenting_options'
import MCPOptions from './multiple_choice_poll_options'

export default class MultipleChoicePoll extends Poll {
  get defaultType () {
    return 'multiple_choice_poll'
  }

  get modelProperties () {
    return super.modelProperties.concat([
      'session_keyword_format',
      'instruction_image_url',
      'instruction_image_alt',
      'responses_per_option'
    ])
  }

  /**
   * Returns the collection for comparison reports. Consumers can access this
   * collection to fetch a list of or individual comparison data.
   * @return {Backbone.Collection.Comparisons}
   */
  get comparisons () {
    return this.instance.comparisons
  }

  initialize (options) {
    super.initialize(...arguments)

    const comparisonReport = this.get('comparison_report') || {}
    this.segmentingOptions = new SegmentingOptions(comparisonReport.groups)
    this.segmentingOptions.poll = this

    this.mcpOptions = new MCPOptions(this.attributes.options, { silent: true })
    this.mcpOptions.poll = this

    this.listenTo(this.mcpOptions, 'add', this.onAddOption)
    this.listenTo(this.mcpOptions, 'remove', this.onRemoveOption)

    this.listenTo(this, 'change:options', this.updateOptions)
  }

  summaryTableUrl () {
    return this._urlBase().segment('table').toString()
  }

  /*
   * Don't call this 'validate' as it will be used as validation for
   * all MCPs
   */
  validateOptions () {
    /*
     * Don't short circuit .isValid() so we can get error status
     * of all the mcpOptions
     */
    if (_.every(this.mcpOptions.invoke('isValid'))) {

    } else {
      return _.unique(_.compact((this.mcpOptions.map(m => m.validationError))))
    }
  }

  hasImageOption () {
    return this.mcpOptions.hasImage()
  }

  hasLatexOption () {
    return this.mcpOptions.hasLatex()
  }

  hasAlphaNumericKeyword () {
    return this.mcpOptions.hasAlphaNumericKeyword()
  }

  highestResponse () {
    return this.mcpOptions && this.mcpOptions.length
      ? this.mcpOptions.highestResult() : null
  }

  onAddOption (option) {
    this.trigger('options:add', option)
  }

  onRemoveOption (option) {
    this.trigger('options:remove', option)
  }

  isGraded () {
    return this.mcpOptions.hasCorrect()
  }

  updateOptions () {
    this.mcpOptions.set(this.get('options'))
  }

  /**
   * Generate a rootless JSON object of all model attrs.
   * @param {Object} options
   * @return {Object}
   */
  toJSON (options = {}) {
    // Grab the generated JSON from the super class
    const json = super.toJSON(options)

    if (this.has('grading_enabled')) {
      json.grading_enabled = this.get('grading_enabled')
    }
    if (this.has('flash_options')) {
      json.flash_options = this.get('flash_options')
    }

    json.options = this.mcpOptions.invoke('toJSON')

    // don't include keywords if simple keywords is enabled and not using custom
    // session keyword format because these are generated from the server
    if (this.isSimpleKeywords() && !this.isCustomSessionKeywordFormat()) {
      for (const option of json.options) {
        delete option.keyword
      }
    }

    return json
  }

  isCustomSessionKeywordFormat () {
    const sessionKeywordFormat = this.get('session_keyword_format') || ''
    return sessionKeywordFormat.toLowerCase() === 'custom'
  }

  parse (json = {}, options = {}) {
    json = super.parse(...arguments)

    options = _.defaults({}, options, {
      resetKeyword: false,
      resetOptions: true
    })

    if (json.options && this.mcpOptions) {
      if (options.resetKeyword) {
        this.mcpOptions.resetKeywords(json.options)
      }

      if (options.resetOptions) {
        // Don't need to go through validation logic here
        options.validate = false
        this.mcpOptions.set(json.options, options)
      } else {
        delete json.options
      }
    }

    /*
     * do this check here because #set uses _.isEqual to determine
     * if this object has changed. _.isEqual only does a shallow comparison
     * so it doesn't detect changes properly
     */
    if (json.comparison_report) {
      if (this.segmentingOptions) {
        this.segmentingOptions.set(json.comparison_report.groups, options)
      }
    }

    return json
  }

  /**
   * Parse Firehose response into the model.
   * @param {object} data Firehose response data.
   * @return {object} Returns potentially mutated data.
   */
  processLiveResponse (data = {}) {
    data = super.processLiveResponse(data)
    this.flashOptions().processLiveResponse(data)
    return data
  }

  toggle (attr, opts = {}, value) {
    if (this.currentArchive) {
      // When archived options are loaded into this model, we need to prevent
      // the `options` collection from being replaced by fresh option data
      // coming back from the `save` response.
      opts = Object.assign({}, opts, {
        resetOptions: false
      })
    }
    return super.toggle(attr, opts, value)
  }

  toggleGradingEnabled (val) {
    if (!val) {
      this.mcpOptions.each(opt =>
        opt.set({
          correct_response: false,
          correct: false
        })
      )
    }

    return this.save({ grading_enabled: val })
  }

  hasCustomGlobalKeyword () {
    return !this.get('simple_keywords') && !this.mcpOptions.all(option => option.isAutoKeyword())
  }
}
